import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {BUSINESS_TYPE_OPTIONS, INDUSTRY_OPTIONS} from '../../client/ClientProfileForm';
import {AgencyClientSelect} from '../../selects/AgencyClientSelect';
import {StepLayout} from '../StepLayout';

const MODE_EXISTING = 'existing';
const MODE_NEW = 'new';

export function Step1ClientData({clientId, initialName, initialBusinessType, initialIndustry, initialMode, onComplete}) {
    const {t} = useTranslation();
    const [mode, setMode] = useState(initialMode || (clientId ? MODE_EXISTING : MODE_NEW));
    const [clients, setClients] = useState(null);
    const [selectedClientId, setSelectedClientId] = useState(clientId || null);
    const [selectedClientName, setSelectedClientName] = useState(initialName || '');
    const [name, setName] = useState(initialName || '');
    const [businessType, setBusinessType] = useState(initialBusinessType || '');
    const [industry, setIndustry] = useState(initialIndustry || '');
    const [loading, setLoading] = useState(Boolean(clientId));
    const [clientsLoading, setClientsLoading] = useState(false);
    const [error, setError] = useState(null);

    const canSubmit = mode === MODE_EXISTING ? Boolean(selectedClientId) : name.trim().length > 0;

    useEffect(() => {
        let cancelled = false;
        setClientsLoading(true);

        new APIClient(API_URL + 'get-agency-data').call('GET').then((response) => {
            if (cancelled) return;
            if (response && response.status === 'OK') {
                setClients(response.clients || []);
            } else {
                setClients([]);
            }
            setClientsLoading(false);
        }).catch(() => {
            if (cancelled) return;
            setClients([]);
            setClientsLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        if (!clientId) {
            setName(initialName || '');
            setBusinessType(initialBusinessType || '');
            setIndustry(initialIndustry || '');
            setLoading(false);
            return undefined;
        }

        setLoading(true);
        setError(null);

        Promise.all([
            new APIClient(API_URL + 'client/' + clientId).call('GET').catch(() => null),
            new APIClient(API_URL + 'client/' + clientId + '/profile').call('GET').catch(() => null),
        ]).then(([clientResponse, profileResponse]) => {
            if (cancelled) return;

            if (clientResponse && clientResponse.status === 'OK') {
                setName(clientResponse.data?.name || '');
            }

            if (profileResponse && profileResponse.status === 'OK') {
                setBusinessType(profileResponse.data?.business_type || '');
                setIndustry(profileResponse.data?.industry || '');
            }

            setLoading(false);
        }).catch((err) => {
            if (cancelled) return;
            setError(err?.message || t('onboarding_wizard.generic_error'));
            setLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [clientId, initialBusinessType, initialIndustry, initialName, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;

        setError(null);

        if (mode === MODE_EXISTING) {
            onComplete(selectedClientId, selectedClientName, {
                mode: MODE_EXISTING,
                name: selectedClientName,
                businessType,
                industry,
            });
            return;
        }

        onComplete(null, name.trim(), {
            mode: MODE_NEW,
            name: name.trim(),
            businessType,
            industry,
        });
    };

    const handleAgencyClientChange = (nextClientId, nextClientName) => {
        setSelectedClientId(nextClientId);
        setSelectedClientName(nextClientName);
    };

    return (
        <StepLayout
            stepNumber={1}
            title={t('onboarding_wizard.step1_title')}
            description={t('onboarding_wizard.step1_description')}
        >
            {loading ? (
                <div className="onboarding-loader">
                    <span className="onboarding-btn-spinner -dark" aria-hidden="true"/>
                </div>
            ) : (
            <form className="onboarding-form" onSubmit={handleSubmit}>
                <div className="onboarding-choice-row">
                    <button
                        type="button"
                        className={`onboarding-choice-card${mode === MODE_EXISTING ? ' -selected' : ''}`}
                        onClick={() => setMode(MODE_EXISTING)}
                        aria-pressed={mode === MODE_EXISTING}
                    >
                        <strong>{t('onboarding_wizard.use_existing_client')}</strong>
                        <span>{t('onboarding_wizard.use_existing_client_description')}</span>
                    </button>
                    <button
                        type="button"
                        className={`onboarding-choice-card${mode === MODE_NEW ? ' -selected' : ''}`}
                        onClick={() => setMode(MODE_NEW)}
                        aria-pressed={mode === MODE_NEW}
                    >
                        <strong>{t('onboarding_wizard.create_new_client')}</strong>
                        <span>{t('onboarding_wizard.create_new_client_description')}</span>
                    </button>
                </div>

                {mode === MODE_EXISTING ? (
                    <div className="onboarding-field">
                        <label>{t('onboarding_wizard.existing_client_label')}</label>
                        <AgencyClientSelect
                            allAgencyClients={clients}
                            loading={clientsLoading}
                            onAgencyClientChange={handleAgencyClientChange}
                            canCreateNewAgencyClient={false}
                            selectedAgencyClient={selectedClientId}
                            showFormValidationTexts={false}
                        />
                    </div>
                ) : (
                    <>
                        <div className="onboarding-field">
                            <label htmlFor="client-name">{t('onboarding_wizard.client_name_label')}</label>
                            <input
                                id="client-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t('onboarding_wizard.client_name_placeholder')}
                                required={mode === MODE_NEW}
                                autoFocus
                            />
                        </div>

                        <div className="onboarding-field-grid">
                            <div className="onboarding-field">
                                <label htmlFor="business-type">{t('onboarding_wizard.business_type_label')}</label>
                                <select
                                    id="business-type"
                                    value={businessType}
                                    onChange={(e) => setBusinessType(e.target.value)}
                                >
                                    <option value="">{t('onboarding_wizard.select_option')}</option>
                                    {BUSINESS_TYPE_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="onboarding-field">
                                <label htmlFor="industry">{t('onboarding_wizard.industry_label')}</label>
                                <select
                                    id="industry"
                                    value={industry}
                                    onChange={(e) => setIndustry(e.target.value)}
                                >
                                    <option value="">{t('onboarding_wizard.select_option')}</option>
                                    {INDUSTRY_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </>
                )}

                {error && <p className="onboarding-error">{error}</p>}

                <div className="onboarding-actions">
                    <button
                        type="submit"
                        className="onboarding-btn -primary"
                        disabled={!canSubmit}
                    >
                        {t('onboarding_wizard.next')}
                    </button>
                </div>
            </form>
            )}
        </StepLayout>
    );
}

export default Step1ClientData;
